import React from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import Button, { ButtonToneProps, ButtonProps } from './Button';
import { OptionItemProps } from '../OptionItem/OptionItem';
import styles from './MenuButton.module.scss';
import { useActionMenuBehavior } from '../../../hooks/useActionMenuBehavior';

export interface MenuButtonProps extends Omit<ButtonProps, 'onClick'> {
  menuItems: OptionItemProps[];
  className?: string;
  title?: string;
  tone: ButtonToneProps;
  disabled?: boolean;
}

const MENU_MIN_WIDTH = 220;

const MenuButton: React.FC<MenuButtonProps> = ({ title, tone, menuItems, className, disabled = false }) => {
  const { triggerRef, menuVisible, setMenuVisible } = useActionMenuBehavior();
  const [coords, setCoords] = React.useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  const toggleMenu = () => {
    if (!disabled) {
      if (!menuVisible && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
      setMenuVisible(!menuVisible);
    }
  };

  const handleItemClick = (item: OptionItemProps, e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    setMenuVisible(false);
    item.onClick?.(e);
  };

  const alignRight = coords.left + Math.max(coords.width, MENU_MIN_WIDTH) > window.innerWidth - 16;

  return (
    <>
      <div className={classNames(styles.wrapper, className)} ref={triggerRef as React.RefObject<HTMLDivElement>}>
        <Button
          tone={tone}
          title={title}
          icon="plus"
          onClick={toggleMenu}
          disabled={disabled}
          trailingIcon="chevronDown"
          className={classNames(styles.menuButton, className)}
        />
      </div>

      {menuVisible &&
        createPortal(
          <div
            className={styles.dropdown}
            role="menu"
            style={{
              top: coords.top,
              left: alignRight ? coords.left + coords.width : coords.left,
              transform: alignRight ? 'translateX(-100%)' : undefined,
              minWidth: coords.width,
              position: 'absolute',
            }}>
            {menuItems.map(item => {
              const isDisabled = item.disabled;
              return (
                <button
                  key={item.label}
                  type="button"
                  className={classNames(styles.menuItem, isDisabled && styles.disabled)}
                  disabled={isDisabled}
                  role="menuitem"
                  tabIndex={0}
                  onClick={e => !isDisabled && handleItemClick(item, e)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !isDisabled) handleItemClick(item, e as any);
                  }}>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
};

export default MenuButton;
